/**
 * Geocoding Utility with PostgreSQL Cache
 * Uses backend API that caches results in PostgreSQL
 * Falls back to Mapbox when cache miss
 */

// In-memory cache for session (supplements PostgreSQL cache)
const sessionCache = new Map<string, string>();

// Richer cache with place_type info
export interface GeocodingResult {
  name: string;
  place_type?: string;
  address?: string;
}
const sessionCacheRich = new Map<string, GeocodingResult>();

// API base URL
const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Batch reverse geocode multiple coordinates
 * Uses backend batch API for parallel processing
 * Chunks requests to stay within API limits (max 100 per batch)
 * Checks session cache first to avoid redundant API calls
 */
export async function batchReverseGeocode(
  coordinates: Array<{ lat: number; lng: number; id: string }>,
  onProgress?: (completed: number, total: number) => void
): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  
  if (coordinates.length === 0) return results;

  const totalCoords = coordinates.length;
  
  // Check session cache first - separate cached from uncached coordinates
  const uncachedCoords: Array<{ lat: number; lng: number; id: string }> = [];
  coordinates.forEach(coord => {
    const cacheKey = `${coord.lat.toFixed(4)},${coord.lng.toFixed(4)}`;
    if (sessionCache.has(cacheKey)) {
      // Use cached result
      results.set(coord.id, sessionCache.get(cacheKey)!);
    } else {
      uncachedCoords.push(coord);
    }
  });
  
  const cachedCount = totalCoords - uncachedCoords.length;
  if (cachedCount > 0) {
    console.log(`[Geocoding] Session cache hit: ${cachedCount}/${totalCoords} locations already cached`);
  }
  
  // Report cached results as immediate progress
  if (onProgress && cachedCount > 0) {
    onProgress(cachedCount, totalCoords);
  }
  
  // If all coordinates were cached, we're done
  if (uncachedCoords.length === 0) {
    console.log(`[Geocoding] All ${totalCoords} locations served from session cache`);
    return results;
  }

  const BATCH_SIZE = 50; // Keep under API limit of 100, smaller for faster progress updates
  let completedCount = cachedCount;

  try {
    // Process uncached coordinates in chunks
    for (let i = 0; i < uncachedCoords.length; i += BATCH_SIZE) {
      const chunk = uncachedCoords.slice(i, i + BATCH_SIZE);
      
      const response = await fetch(`${API_BASE}/geocoding/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coordinates: chunk.map(c => ({ lat: c.lat, lng: c.lng }))
        })
      });

      if (!response.ok) {
        console.error(`Batch geocoding failed for chunk ${i}-${i + chunk.length}:`, response.status);
        completedCount += chunk.length;
        if (onProgress) onProgress(completedCount, totalCoords);
        continue; // Skip this chunk but continue with others
      }

      const data = await response.json();
      
      // Map results back to IDs
      data.forEach((result: any, index: number) => {
        const originalCoord = chunk[index];
        if (result.name && originalCoord) {
          results.set(originalCoord.id, result.name);
          const cacheKey = `${originalCoord.lat.toFixed(4)},${originalCoord.lng.toFixed(4)}`;
          sessionCache.set(cacheKey, result.name);
          // Also store richer data
          sessionCacheRich.set(cacheKey, {
            name: result.name,
            place_type: result.place_type,
            address: result.address
          });
        }
      });

      completedCount += chunk.length;
      if (onProgress) {
        onProgress(completedCount, totalCoords);
      }
      
      console.log(`[Geocoding] Chunk ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(uncachedCoords.length / BATCH_SIZE)} completed`);
    }

    console.log(`[Geocoding] Batch completed: ${results.size}/${totalCoords} locations named (${cachedCount} cached, ${uncachedCoords.length} fetched)`);
    
  } catch (error) {
    console.error('Batch geocoding error:', error);
  }

  return results;
}

/**
 * Batch reverse geocode with full result data (including place_type)
 * Returns richer GeocodingResult objects instead of just names
 * Checks session cache first to avoid redundant API calls
 */
export async function batchReverseGeocodeRich(
  coordinates: Array<{ lat: number; lng: number; id: string }>,
  onProgress?: (completed: number, total: number) => void
): Promise<Map<string, GeocodingResult>> {
  const results = new Map<string, GeocodingResult>();
  
  if (coordinates.length === 0) return results;

  const totalCoords = coordinates.length;
  
  // Check session cache first - separate cached from uncached coordinates
  const uncachedCoords: Array<{ lat: number; lng: number; id: string }> = [];
  coordinates.forEach(coord => {
    const cacheKey = `${coord.lat.toFixed(4)},${coord.lng.toFixed(4)}`;
    if (sessionCacheRich.has(cacheKey)) {
      // Use cached rich result
      results.set(coord.id, sessionCacheRich.get(cacheKey)!);
    } else if (sessionCache.has(cacheKey)) {
      // Fall back to basic cache (name only)
      results.set(coord.id, { name: sessionCache.get(cacheKey)! });
    } else {
      uncachedCoords.push(coord);
    }
  });
  
  const cachedCount = totalCoords - uncachedCoords.length;
  if (cachedCount > 0) {
    console.log(`[Geocoding] Session cache hit: ${cachedCount}/${totalCoords} locations already cached`);
  }
  
  // Report cached results as immediate progress
  if (onProgress && cachedCount > 0) {
    onProgress(cachedCount, totalCoords);
  }
  
  // If all coordinates were cached, we're done
  if (uncachedCoords.length === 0) {
    console.log(`[Geocoding] All ${totalCoords} locations served from session cache`);
    return results;
  }

  const BATCH_SIZE = 50;
  let completedCount = cachedCount;

  try {
    // Process uncached coordinates in chunks
    for (let i = 0; i < uncachedCoords.length; i += BATCH_SIZE) {
      const chunk = uncachedCoords.slice(i, i + BATCH_SIZE);
      
      const response = await fetch(`${API_BASE}/geocoding/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coordinates: chunk.map(c => ({ lat: c.lat, lng: c.lng }))
        })
      });

      if (!response.ok) {
        console.error(`Batch geocoding failed for chunk ${i}-${i + chunk.length}:`, response.status);
        completedCount += chunk.length;
        if (onProgress) onProgress(completedCount, totalCoords);
        continue;
      }

      const data = await response.json();
      
      data.forEach((result: any, index: number) => {
        const originalCoord = chunk[index];
        if (result.name && originalCoord) {
          const geocodingResult: GeocodingResult = {
            name: result.name,
            place_type: result.place_type,
            address: result.address
          };
          results.set(originalCoord.id, geocodingResult);
          
          const cacheKey = `${originalCoord.lat.toFixed(4)},${originalCoord.lng.toFixed(4)}`;
          sessionCache.set(cacheKey, result.name);
          sessionCacheRich.set(cacheKey, geocodingResult);
        }
      });

      completedCount += chunk.length;
      if (onProgress) onProgress(completedCount, totalCoords);
    }

    console.log(`[Geocoding] Rich batch completed: ${results.size}/${totalCoords} locations (${cachedCount} cached, ${uncachedCoords.length} fetched)`);
    
  } catch (error) {
    console.error('Rich batch geocoding error:', error);
  }

  return results;
}